/**
 * Arm-combination domain rules for Treatment Configuration. An experiment runs
 * a *subset* of arms (A = Control, B / C = Lily-enriched variants), not just a
 * count — so which arms are in play is the source of truth and the arm count is
 * derived from it. See `docs/treatment-configuration.md` → "Arm combinations".
 */

import type {
  ArmCombination,
  ArmCount,
  ArmId,
  VariantArmId,
} from '../types/treatment-configuration.types';

/** Arms included in each supported combination, in display order. */
const ARMS_BY_COMBINATION: Record<ArmCombination, readonly ArmId[]> = {
  A_B: ['A', 'B'],
  A_C: ['A', 'C'],
  B_C: ['B', 'C'],
  A_B_C: ['A', 'B', 'C'],
};

/** The 2-arm pairs offered by the "Which arms?" selector, in display order. */
export const TWO_ARM_COMBINATIONS: readonly ArmCombination[] = ['A_B', 'A_C', 'B_C'];

/** The single 3-arm combination. Selecting "3 Arms" always means Control + B + C. */
export const THREE_ARM_COMBINATION: ArmCombination = 'A_B_C';

/** Combination used for a platform that has never been configured, and the
 *  fallback when a 2-arm record predates the `arms` wire field. */
export const DEFAULT_COMBINATION: ArmCombination = 'A_B';

/**
 * Combinations agentic-config accepts on a write regardless of its
 * `ENABLE_ARM_COMBINATIONS` flag — every config that existed before this feature
 * is one of these. Used as the fallback when a response omits
 * `supported_arm_combinations`, so an older server never gets offered a
 * combination it would reject with a 400.
 */
export const ALWAYS_WRITABLE_COMBINATIONS: readonly ArmCombination[] = ['A_B', 'A_B_C'];

const ALL_COMBINATIONS = new Set<string>(['A_B', 'A_C', 'B_C', 'A_B_C']);

/**
 * Narrow the wire's `supported_arm_combinations` to known combination keys.
 * Unrecognised entries from a newer server are dropped rather than trusted, and
 * an absent/empty/all-unknown list falls back to `ALWAYS_WRITABLE_COMBINATIONS`
 * — the selector must never offer something the server will reject.
 */
export function parseSupportedCombinations(
  values: readonly string[] | undefined,
): ArmCombination[] {
  const known = (values ?? []).filter((v): v is ArmCombination => ALL_COMBINATIONS.has(v));
  return known.length > 0 ? known : [...ALWAYS_WRITABLE_COMBINATIONS];
}

/** Arms included in a combination, in display order. */
export function armsForCombination(combination: ArmCombination): readonly ArmId[] {
  return ARMS_BY_COMBINATION[combination];
}

/** Whether an arm runs in this combination — drives which rows the page renders. */
export function combinationIncludes(combination: ArmCombination, arm: ArmId): boolean {
  return ARMS_BY_COMBINATION[combination].includes(arm);
}

/** Arm count, derived. Never stored alongside the combination to avoid two
 *  sources of truth that can disagree. */
export function armCountForCombination(combination: ArmCombination): ArmCount {
  return ARMS_BY_COMBINATION[combination].length as ArmCount;
}

/** True when the combination has no Control arm (B vs C) — the page warns on
 *  this because without a holdout only relative, not absolute, lift is measurable. */
export function lacksControl(combination: ArmCombination): boolean {
  return !combinationIncludes(combination, 'A');
}

/** The enriched variant arms in play — the arms that need a treatment picked. */
export function variantArmsForCombination(combination: ArmCombination): readonly VariantArmId[] {
  return ARMS_BY_COMBINATION[combination].filter((arm): arm is VariantArmId => arm !== 'A');
}

/**
 * Wire `arms` array → combination key. Order-insensitive so the backend is free
 * to send `["C","A"]`. Returns `null` for anything unsupported (unknown letters,
 * duplicates, a single arm, a future 4-arm shape) so callers fall back rather
 * than crashing on a payload from a newer backend.
 */
export function combinationForArms(arms: readonly string[] | undefined): ArmCombination | null {
  if (!arms?.length) return null;
  const unique = new Set(arms);
  if (unique.size !== arms.length) return null;
  const found = (Object.keys(ARMS_BY_COMBINATION) as ArmCombination[]).find((key) => {
    const expected = ARMS_BY_COMBINATION[key];
    return expected.length === unique.size && expected.every((arm) => unique.has(arm));
  });
  return found ?? null;
}

/**
 * Resolve the combination for a GET response. Prefers the explicit `arms` field;
 * falls back to the legacy `num_arms`-only shape (3 ⇒ A_B_C, anything else ⇒
 * A_B) so the page keeps working against a backend that hasn't shipped `arms`
 * yet. Legacy 2-arm records are always Control-vs-B — the only shape that was
 * expressible before this feature.
 */
export function resolveCombination(
  arms: readonly string[] | undefined,
  numArms: ArmCount,
): ArmCombination {
  return combinationForArms(arms) ?? (numArms === 3 ? THREE_ARM_COMBINATION : DEFAULT_COMBINATION);
}
